import cloneDeep from 'lodash/cloneDeep';

/**
 * @param object { [key: string]: unknown } Any object.
 * @param name Name of the property/key to look up in the object keys collection.
 * @param caseSensitive (default false) Determine if the search is case sensitive or case insensitive.
 */
interface PropertySearchParams {
    object: unknown;
    name: string;
    caseSensitive?: boolean;
}

const DefaultPropertySearchParams = {
    object: {},
    name: '',
    caseSensitive: false,
};

const isObject = (value: unknown) => typeof value === 'object' && !Array.isArray(value) && value !== null;
const asRecord = (value: unknown) => ({ ...(value as Record<string, unknown>) });

/**
 *
 * @param params @see PropertySearchParams
 * @returns string The first occurence of the _name_ in the right case.
 */
export function findPropertyName(params: PropertySearchParams): string | undefined {
    const { object, name, caseSensitive } = { ...DefaultPropertySearchParams, ...params };
    if (!isObject(object) || !name) {
        return undefined;
    }
    const sensitive = (s: string) => s === name;
    const insensitive = (i: string) => i.toLocaleLowerCase() === name.toLocaleLowerCase();
    return Object.keys(asRecord(object)).find(caseSensitive ? sensitive : insensitive);
}

/**
 *
 * @param params @see PropertySearchParams
 * @returns T The value of the property
 */
export function getPropertyValue<T>({ object, name, caseSensitive }: PropertySearchParams): T | undefined {
    const propertyName = findPropertyName({ object, name, caseSensitive });
    return propertyName ? (asRecord(object)[propertyName] as T) : undefined;
}

/**
 * Given a "shape" (case sensitive) object, it reads the property values from a origin object.
 * Commonly use to map DTO to Default Object structures.
 * @param origin Any Object
 * @param destinationShape An initializated object with the expected attributes to copy from the source object.
 * @param caseSensitive Indicate if the lookup of the properties should be case sensitive
 * @return Record<string, unknown> A populated object with the destination "shape"
 */
export function ShapeObject<O, D>(origin: O, destinationShape: D, caseSensitive = false): D {
    const result: Record<string, unknown> = {};

    if (!isObject(origin) || !isObject(destinationShape)) {
        return result as D;
    }

    const shape = asRecord(destinationShape);
    Object.keys(shape).forEach(key => {
        const value = getPropertyValue({ object: origin, name: key, caseSensitive });
        result[key] = isObject(value) ? ShapeObject(value, shape[key]) : value;
    });

    return result as D;
}

export function removeFN(obj: unknown): unknown {
    const cloneObj = cloneDeep(obj);

    if (!isObject(cloneObj)) {
        return cloneObj;
    }

    const result = asRecord(cloneObj);
    Object.keys(result).forEach(key => {
        if (typeof result[key] === 'function') {
            delete result[key];
        } else if (isObject(result[key])) {
            result[key] = removeFN(result[key]);
        }
    });

    return result;
}
